import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import convertDateFetch from "../../../HelpersFunctions/dateAndTime/convertDateFetch";
import useFetchAndSetGET from "../../../hooks/fetchHooks/useFetchAndSetGET/useFetchAndSetGET";
import { selectSettings } from "../../../reducers/settings";
import { useAppSelector } from "../../../store/hooks";
import { DatePickerFormik, TextFieldFormik } from "../FormikInputs";
import LoadingWrapper from "../LoadingWrapper/loadingWrapper";
import {
  DialogContentStyled,
  Row,
  RowInput,
  RowTitle,
  TwoColumnsRowsContainer,
} from "../PopupHelpers";

const PopupContentSupport = ({
  employeeId,
  onSubmit,
  listOfEmployeesId,
  pathApiListsOfEmployees,
}: {
  employeeId?: number;
  onSubmit: (values: IEditEmployeeInListOfEmployees) => void;
  listOfEmployeesId?: number;
  pathApiListsOfEmployees: string;
}) => {
  const { t } = useTranslation();

  const [fetchedEmployee, setFetchedEmployee] =
    useState<IListOfEmployeesEmployee | null>(null);

  const [fetchedRow, fetchingStateFetchedRow, fetchAgainFetchedRow] =
    useFetchAndSetGET<IListOfEmployees>({
      path: `${pathApiListsOfEmployees}/${listOfEmployeesId}`,
      startFetchOnInitial: listOfEmployeesId ? true : false,
    });

  useEffect(() => {
    if (fetchingStateFetchedRow.response?.status === 200 && employeeId) {
      const employee = fetchedRow?.employess?.find(
        (emp) => emp.id === employeeId
      );
      if (employee) {
        setFetchedEmployee(employee);
      }
    }
  }, [fetchingStateFetchedRow.response?.status, fetchedRow, employeeId]);

  //#region Domyślna nazwa firmy
  const settings = useAppSelector(selectSettings);
  const defaultCompanyName = settings["DomyslnaFirmaListaPracownikow"]?.value
    ? settings["DomyslnaFirmaListaPracownikow"].value
    : "";
  //#endregion

  return (
    <div>
      <LoadingWrapper
        isLodadingProgress={fetchingStateFetchedRow.isFetching}
        isError={fetchingStateFetchedRow.isError}
        setIfFetchAgain={() => {
          if (fetchingStateFetchedRow.isError) {
            fetchAgainFetchedRow();
          }
        }}
      >
        {(!employeeId || (fetchedRow && fetchedEmployee)) && (
          <DialogContentStyled>
            <Formik
              initialValues={{
                date: fetchedRow?.date ? new Date(fetchedRow.date) : null,
                timeFrom: fetchedRow?.timeFrom ? fetchedRow.timeFrom : "",
                timeTo: fetchedRow?.timeTo ? fetchedRow.timeTo : "",
                employeeFirstName: fetchedEmployee?.employeeFirstName
                  ? fetchedEmployee.employeeFirstName
                  : "",
                employeeLastName: fetchedEmployee?.employeeLastName
                  ? fetchedEmployee.employeeLastName
                  : "",
                employeeIDNumber: fetchedEmployee?.employeeIDNumber
                  ? fetchedEmployee.employeeIDNumber
                  : "",
                company: fetchedEmployee?.company
                  ? fetchedEmployee.company
                  : defaultCompanyName,
                position: fetchedEmployee?.position
                  ? fetchedEmployee.position
                  : "",
                comments: fetchedEmployee?.comments
                  ? fetchedEmployee.comments
                  : "",
              }}
              onSubmit={(values) => {
                const object = {
                  date: convertDateFetch(values.date!),
                  timeFrom: values.timeFrom,
                  timeTo: values.timeTo ? values.timeTo : null,
                  employeeFirstName: values.employeeFirstName,
                  employeeLastName: values.employeeLastName,
                  employeeIDNumber: values.employeeIDNumber,
                  company: values.company,
                  position: values.position,
                  comments: values.comments,
                };
                onSubmit(object);
              }}
              validationSchema={Yup.object({
                date: Yup.date().nullable().required(t("field_required")),
                timeFrom: Yup.string().required(t("field_required")),
                employeeFirstName: Yup.string().required(t("field_required")),
                employeeLastName: Yup.string().required(t("field_required")),
                position: Yup.string().required(t("field_required")),
                company: Yup.string().required(t("field_required")),
              })}
            >
              <Form noValidate id="submitForm">
                {/*  */}
                <TwoColumnsRowsContainer>
                  <Row>
                    <RowTitle>{t("date")}:</RowTitle>
                    <RowInput>
                      <DatePickerFormik
                        label={t("date")}
                        name="date"
                        view={["year", "month", "date"]}
                        format="dd-MM-yyyy"
                        keyboardDateTimePicker={true}
                        required={true}
                      />
                    </RowInput>
                  </Row>
                </TwoColumnsRowsContainer>

                {/*  */}
                <TwoColumnsRowsContainer>
                  <Row>
                    <RowTitle>{t("time_from")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        name="timeFrom"
                        hiddenLabel
                        type="time"
                        required={true}
                      />
                    </RowInput>
                  </Row>

                  <Row>
                    <RowTitle>{t("time_to")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik name="timeTo" hiddenLabel type="time" />
                    </RowInput>
                  </Row>
                </TwoColumnsRowsContainer>

                {/*  */}
                <TwoColumnsRowsContainer>
                  <Row>
                    <RowTitle>{t("first_name")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("first_name")}
                        name="employeeFirstName"
                        type="text"
                        required={true}
                        ifToUpperCaseShipsAndPorts={true}
                      />
                    </RowInput>
                  </Row>

                  <Row>
                    <RowTitle>{t("last_name")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("last_name")}
                        name="employeeLastName"
                        type="text"
                        required={true}
                        ifToUpperCaseShipsAndPorts={true}
                      />
                    </RowInput>
                  </Row>
                </TwoColumnsRowsContainer>

                {/*  */}
                <TwoColumnsRowsContainer>
                  <Row>
                    <RowTitle>{t("id_number")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("id_number")}
                        name="employeeIDNumber"
                        type="text"
                        ifToUpperCaseShipsAndPorts={true}
                      />
                    </RowInput>
                  </Row>

                  <Row>
                    <RowTitle>{t("company")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("company")}
                        name="company"
                        type="text"
                        ifToUpperCaseShipsAndPorts={true}
                        required={true}
                      />
                    </RowInput>
                  </Row>
                </TwoColumnsRowsContainer>

                {/*  */}
                <TwoColumnsRowsContainer>
                  <Row>
                    <RowTitle>{t("position")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("position")}
                        name="position"
                        type="text"
                        ifToUpperCaseShipsAndPorts={true}
                        required={true}
                      />
                    </RowInput>
                  </Row>

                  <Row>
                    <RowTitle>{t("comments")}:</RowTitle>
                    <RowInput>
                      <TextFieldFormik
                        label={t("comments")}
                        name="comments"
                        type="text"
                        ifToUpperCaseShipsAndPorts={true}
                        multiline={true}
                        rows={3}
                      />
                    </RowInput>
                  </Row>
                </TwoColumnsRowsContainer>
              </Form>
            </Formik>
          </DialogContentStyled>
        )}
      </LoadingWrapper>
    </div>
  );
};

export default PopupContentSupport;
